import is from '@sindresorhus/is';
import { type FunctionComponent, type ReactNode, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { Translate } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconArchive, IconThreeDots } from '~/ui/core/icons';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';
import { Textarea } from '~/ui/core/textarea/Textarea';

import type { GrowthGoal, GrowthGoalDetail } from '../../data-contract/growth';
import { useGoalAction } from '../api/growth-mutations';
import { useGoalDetail } from '../api/growth-queries';
import { AgentButton } from '../components/AgentButton';
import { DetailSection, GrowthTileFrame, GrowthTileSkeleton } from '../components/GrowthTileFrame';
import { GoalStatusBadge } from '../components/goal-display';
import { PANEL_TITLE_CLASS_NAME } from '../components/panel-title';
import { CheckpointsSection } from './CheckpointsSection';
import { CommentsSection } from './CommentsSection';
import { CompanyGoalSection } from './CompanyGoalSection';
import { ProgressSection } from './ProgressSection';
import { ReviewSection } from './ReviewSection';

const SendBackForm: FunctionComponent<{
    readonly goal: GrowthGoal;
    readonly onCancel: () => void;
    readonly onSent: () => void;
}> = ({ goal, onCancel, onSent }) => {
    const translate = useTranslate();
    const goalAction = useGoalAction(goal.id);
    const [comment, setComment] = useState('');

    return (
        <div className="flex w-full flex-col gap-m">
            <Textarea
                aria-label={translate('pages.growth.detail.send-back-placeholder')}
                autoResize={true}
                placeholder={translate('pages.growth.detail.send-back-placeholder')}
                rows={2}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
            />
            <div className="flex justify-end gap-m">
                <Button size="sm" variant="tertiary" onClick={onCancel}>
                    {translate('common.cancel')}
                </Button>
                <Button
                    disabled={comment.trim().length === 0 || goalAction.isPending}
                    size="sm"
                    variant="primary"
                    onClick={() =>
                        goalAction.mutate({ action: 'send-back', comment: comment.trim() }, { onSuccess: onSent })
                    }
                >
                    {translate('pages.growth.actions.send-back')}
                </Button>
            </div>
        </div>
    );
};

type GoalAction = ReturnType<typeof useGoalAction>;

const panelActions = ({
    goal,
    goalAction,
    onSendBack,
    translate,
    viewerRole,
}: {
    readonly goal: GrowthGoal;
    readonly goalAction: GoalAction;
    readonly onSendBack: () => void;
    readonly translate: Translate;
    readonly viewerRole: GrowthGoalDetail['viewerRole'];
}): ReactNode[] => {
    const disabled = goalAction.isPending;
    const button = (label: string, action: 'agree' | 'keep-private' | 'mark-done' | 'share', isPrimary = false) => (
        <Button
            key={action}
            disabled={disabled}
            size="sm"
            variant={isPrimary ? 'primary' : 'secondary'}
            onClick={() => goalAction.mutate({ action })}
        >
            {label}
        </Button>
    );

    if (viewerRole === 'viewer') {
        return [];
    }
    if (viewerRole === 'manager') {
        if (goal.state === 'in-review' && !is.nullOrUndefined(goal.outcome)) {
            return [
                <Button
                    key="confirm-outcome"
                    disabled={disabled}
                    size="sm"
                    variant="primary"
                    onClick={() => goalAction.mutate({ action: 'confirm-outcome' })}
                >
                    {translate('pages.growth.actions.confirm-outcome')}
                </Button>,
            ];
        }

        return goal.state === 'waiting-for-agreement'
            ? [
                  <Button key="send-back" disabled={disabled} size="sm" variant="secondary" onClick={onSendBack}>
                      {translate('pages.growth.actions.send-back')}
                  </Button>,
                  button(translate('pages.growth.actions.agree'), 'agree', true),
              ]
            : [];
    }
    if (goal.state === 'draft') {
        return [
            button(translate('pages.growth.actions.keep-private'), 'keep-private'),
            button(translate('pages.growth.actions.share-with-manager'), 'share', true),
        ];
    }
    if (goal.state === 'open') {
        return goal.visibility === 'private'
            ? [
                  button(translate('pages.growth.actions.mark-done'), 'mark-done'),
                  button(translate('pages.growth.actions.share-with-manager'), 'share', true),
              ]
            : [button(translate('pages.growth.actions.mark-done'), 'mark-done')];
    }

    return [];
};

const ArchiveMenu: FunctionComponent<{
    readonly goal: GrowthGoal;
    readonly goalAction: GoalAction;
    readonly onArchived: () => void;
}> = ({ goal, goalAction, onArchived }) => {
    const translate = useTranslate();
    const [isConfirming, setIsConfirming] = useState(false);

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button
                        aria-label={translate('pages.growth.actions.more-actions')}
                        iconOnly={true}
                        size="sm"
                        variant="tertiary"
                    >
                        <IconThreeDots />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={4}>
                    <DropdownMenuItem disabled={goalAction.isPending} onSelect={() => setIsConfirming(true)}>
                        <IconArchive size={16} />
                        {translate('pages.growth.actions.archive')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('pages.growth.actions.archive-confirm')}
                confirmButtonVariant="default"
                description={translate('pages.growth.actions.archive-description')}
                open={isConfirming}
                title={translate('pages.growth.actions.archive-title', { title: goal.title })}
                onCancel={() => setIsConfirming(false)}
                onConfirm={() => {
                    setIsConfirming(false);
                    goalAction.mutate({ action: 'archive' }, { onSuccess: onArchived });
                }}
            />
        </>
    );
};

const LearningProposal: FunctionComponent<{ readonly goal: GrowthGoal }> = ({ goal }) => {
    const translate = useTranslate();
    const [isTaken, setIsTaken] = useState(false);

    if (is.nullOrUndefined(goal.learningProposal)) {
        return null;
    }

    return (
        <div className="flex w-full flex-col gap-m rounded-xl border border-border-subtle p-xl">
            <span className="text-body-xs font-medium text-text-muted">
                {translate('pages.growth.detail.learning-hint')}
            </span>
            <div className="flex flex-wrap items-center justify-between gap-m">
                <div className="flex flex-col gap-xs">
                    <span className="text-body-m font-medium text-text-heading">{goal.learningProposal.title}</span>
                    <span className="text-body-s text-text-secondary">{goal.learningProposal.duration}</span>
                </div>
                <Button disabled={isTaken} size="sm" variant="secondary" onClick={() => setIsTaken(true)}>
                    {translate(isTaken ? 'pages.growth.detail.learning-taken' : 'pages.growth.detail.learning-take')}
                </Button>
            </div>
        </div>
    );
};

const GoalDetailContent: FunctionComponent<{ readonly detail: GrowthGoalDetail; readonly onClose: () => void }> = ({
    detail,
    onClose,
}) => {
    const translate = useTranslate();
    const { companyGoals, goal, owner, viewerRole } = detail;
    const [isSendingBack, setIsSendingBack] = useState(false);
    const isOwner = viewerRole === 'owner';
    const goalAction = useGoalAction(goal.id);
    const actions = panelActions({
        goal,
        goalAction,
        onSendBack: () => setIsSendingBack(true),
        translate,
        viewerRole,
    });
    const sendBackForm = isSendingBack ? (
        <SendBackForm goal={goal} onCancel={() => setIsSendingBack(false)} onSent={onClose} />
    ) : null;

    return (
        <GrowthTileFrame
            actions={
                viewerRole === 'viewer' ? null : (
                    <>
                        <AgentButton label={translate('pages.growth.actions.ask-agent')} size="sm" />
                        {isSendingBack ? null : actions}
                        <ArchiveMenu goal={goal} goalAction={goalAction} onArchived={onClose} />
                    </>
                )
            }
            onClose={onClose}
        >
            <header className="flex w-full flex-col gap-group">
                <div className="flex flex-col gap-s">
                    <h2 className={PANEL_TITLE_CLASS_NAME}>{goal.title}</h2>
                    <div className="flex flex-wrap items-center gap-x-m gap-y-xs">
                        {isOwner ? null : <span className="text-body-xs text-text-secondary">{owner.name}</span>}
                        <GoalStatusBadge goal={goal} />
                    </div>
                </div>
                {sendBackForm}
            </header>

            {isOwner && goal.state === 'draft' && is.nonEmptyString(goal.returnComment) ? (
                <div className="flex w-full flex-col gap-xs rounded-xl bg-status-warning-surface p-xl">
                    <span className="text-body-xs font-medium text-status-warning-text">
                        {translate('pages.growth.detail.returned')}
                    </span>
                    <span className="text-body-s text-text-primary">{goal.returnComment}</span>
                </div>
            ) : null}

            {is.nonEmptyString(goal.description) ? (
                <DetailSection title={translate('pages.growth.detail.description')}>
                    <RichEditor
                        ariaLabel={translate('pages.growth.detail.description')}
                        contentClassName="min-h-0! bg-transparent p-0! [&_.editor-paragraph:last-child]:mb-0"
                        disabled={true}
                        editableClassName="min-h-0 text-text-primary"
                        toolbar={null}
                        value={goal.description}
                    />
                </DetailSection>
            ) : null}

            <ProgressSection goal={goal} />

            <CompanyGoalSection canEdit={isOwner && goal.state !== 'done'} companyGoals={companyGoals} goal={goal} />

            {isOwner ? <LearningProposal goal={goal} /> : null}

            <ReviewSection goal={goal} viewerRole={viewerRole} />

            <CheckpointsSection canUpdate={isOwner && goal.state === 'open'} goal={goal} />

            <CommentsSection canComment={viewerRole !== 'viewer'} goal={goal} />
        </GrowthTileFrame>
    );
};

export const GoalDetailTile: FunctionComponent<{ readonly goalId: string; readonly onClose: () => void }> = ({
    goalId,
    onClose,
}) => {
    const translate = useTranslate();
    const detailQuery = useGoalDetail(goalId);

    if (detailQuery.isPending) {
        return <GrowthTileSkeleton onClose={onClose} />;
    }

    if (detailQuery.isError) {
        return (
            <GrowthTileFrame onClose={onClose}>
                <p className="m-0 text-body-s text-text-secondary">{translate('pages.growth.load-error')}</p>
            </GrowthTileFrame>
        );
    }

    return <GoalDetailContent detail={detailQuery.data} onClose={onClose} />;
};
